import React from "react"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import {
  useNavigationContext,
  type NavigationContextValue
} from "./navigation-context"

export type NavigationBadgeType = "paid" | "beta"

export const BADGE_ICONS: Record<NavigationBadgeType, IconName> = {
  paid: "diamond",
  beta: "beta-beaker"
}

/**
 * A `"paid"` badge is meaningless for accounts that already pay, so it's
 * hidden for them — both the inline icon (`NavigationBadge`) and the tooltip
 * callout (`NavigationTooltip`) gate on this so the two stay in lockstep.
 */
export function isPaidBadgeHidden(
  type: NavigationBadgeType,
  accountType: NavigationContextValue["accountType"]
): boolean {
  return type === "paid" && accountType === "paid"
}

/**
 * Inline badge icon rendered inside `Navigation.Item`, `Navigation.Group`,
 * and `Navigation.GroupItem` when a `badge` prop is set. Maps the semantic
 * type to the corresponding icon. Internal — used only via the `badge`
 * prop on the three navigation row primitives.
 *
 * Paid accounts (via `Navigation`'s `accountType` prop) suppress the
 * `"paid"` icon.
 */
export function NavigationBadge({ type }: { type: NavigationBadgeType }) {
  const { accountType } = useNavigationContext()

  if (isPaidBadgeHidden(type, accountType)) return null

  return (
    <Icon
      name={BADGE_ICONS[type]}
      // eslint-disable-next-line rulesdir/no-deprecated-icon-size
      size="sm"
      className="shrink-0 text-gray-700"
      aria-hidden
    />
  )
}
